import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { createAssessment } from "@/lib/api";
import NavBar from "@/components/NavBar";
import { Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const INDUSTRIES = [
  "Plumbing",
  "Electrical",
  "HVAC",
  "Roofing",
  "Landscaping",
  "General Contractor",
  "Cleaning",
  "Auto",
  "Dental",
  "Medical",
  "Legal",
  "Real Estate",
  "Salon",
  "Restaurant",
  "Other",
];

export default function NewAssessment() {
  const navigate = useNavigate();
  const [businessName, setBusinessName] = useState("");
  const [website, setWebsite] = useState("");
  const [industry, setIndustry] = useState("Plumbing");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!businessName.trim() || !website.trim() || !industry.trim()) {
      toast.error("Please fill out every field to continue.");
      return;
    }
    setSubmitting(true);
    const t = toast.loading("Starting async assessment…", {
      description: "Deterministic scoring first, then Claude interprets the findings live.",
    });
    try {
      const data = await createAssessment({
        business_name: businessName.trim(),
        website: website.trim(),
        industry: industry.trim(),
      });
      toast.success("Assessment started", { id: t, description: "Streaming live progress…" });
      navigate(`/assessment/${data.assessment_id}/progress`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Assessment failed. Please try again.";
      toast.error(msg, { id: t });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1115] text-white">
      <NavBar />
      <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-14">
        <div className="mb-10">
          <div className="pp-label mb-3">New Trust Assessment</div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight text-white">
            Tell us about the business.
            <span className="block text-[#6CC24A]">We'll do the honest math.</span>
          </h1>
          <p className="mt-5 text-[#C7CBD1] max-w-2xl leading-relaxed">
            In under thirty seconds, you'll see a full trust breakdown across Google
            Search, Google Maps, AI answers, and the business's own website — plus
            a prioritized list of what to fix first.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="pp-card p-8 max-w-2xl"
          data-testid="new-assessment-form"
        >
          <div className="space-y-6">
            <div>
              <label className="pp-label block mb-2" htmlFor="biz">
                Business Name
              </label>
              <input
                id="biz"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="e.g., Tri-County Plumbing"
                data-testid="input-business-name"
                className="w-full bg-[#0F1115] border border-[#333C47] px-4 py-3 text-white focus:outline-none focus:border-[#6CC24A]"
              />
            </div>
            <div>
              <label className="pp-label block mb-2" htmlFor="site">
                Website
              </label>
              <input
                id="site"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="e.g., tricountyplumbing.com"
                data-testid="input-website"
                className="w-full bg-[#0F1115] border border-[#333C47] px-4 py-3 text-white focus:outline-none focus:border-[#6CC24A]"
              />
            </div>
            <div>
              <label className="pp-label block mb-2" htmlFor="ind">
                Industry
              </label>
              <Select value={industry} onValueChange={setIndustry}>
                <SelectTrigger
                  id="ind"
                  data-testid="input-industry"
                  className="w-full bg-[#0F1115] border border-[#333C47] rounded-none px-4 py-3 h-auto text-white focus:outline-none focus:border-[#6CC24A] focus:ring-0"
                >
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent
                  className="bg-[#1A1F24] border border-[#333C47] text-white rounded-none max-h-72"
                  data-testid="industry-options"
                >
                  {INDUSTRIES.map((i) => (
                    <SelectItem
                      key={i}
                      value={i}
                      data-testid={`industry-option-${i.toLowerCase().replace(/\s+/g, "-")}`}
                      className="rounded-none focus:bg-[#232931] focus:text-white text-white cursor-pointer"
                    >
                      {i}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-[#C7CBD1]">
              <ShieldCheck className="h-3.5 w-3.5 text-[#6CC24A]" />
              Your business info is never sold or shared.
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-assessment"
              className="pp-btn-primary flex items-center gap-2 disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Running assessment
                </>
              ) : (
                <>
                  Run assessment <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
